import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Activity, AlertTriangle, TrendingUp, Calendar,
  Bell, Clock, CheckCircle, ChevronRight, User, Mail,
  Phone, Shield, Eye, Settings, RefreshCw, Heart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../services/api';

const API_URL = getApiBaseUrl();

export default function ParentPortalPage() {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('token') || '');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [myLinks, setMyLinks] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]); // Keep for backward compatibility but unused
  
  // Form states
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkForm, setLinkForm] = useState({
    student_email: '',
    parent_name: '',
    relationship: 'parent',
    phone: ''
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChildren();
    fetchMyLinks();
    // Removed fetchPendingLinks - approval flow no longer needed
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchDashboard(selectedChild.student_id);
    }
  }, [selectedChild]);

  const fetchChildren = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/parent/children`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setChildren(Array.isArray(data) ? data : []);
      if (data.length > 0 && !selectedChild) {
        setSelectedChild(data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch children:', err);
    }
  };

  const fetchMyLinks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/parent/my-links`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMyLinks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch links:', err);
    }
  };

  // Removed fetchPendingLinks - approval flow simplified

  const fetchDashboard = async (studentId) => {
    try {
      const res = await fetch(`${API_URL}/api/prep-os/parent/dashboard/${studentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.student) {
        setDashboard(data);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  };

  const requestLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/prep-os/parent/link/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(linkForm)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to request link');
      }
      
      toast.success('Successfully linked to student! You can now view their progress.');
      setShowLinkForm(false);
      setLinkForm({ student_email: '', parent_name: '', relationship: 'parent', phone: '' });
      fetchMyLinks();
      fetchChildren(); // Refresh to show newly linked child
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Approve/reject functions removed - links are now auto-approved

  const getRiskColor = (level) => {
    switch (level) {
      case 'RED': return 'bg-red-100 text-red-700 border-red-300';
      case 'YELLOW': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-green-100 text-green-700 border-green-300';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/30" data-testid="parent-portal-page">
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-3 mb-2">
            <div className="p-2 md:p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
              <Heart className="h-6 w-6 md:h-8 md:w-8 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Parent Portal
              </h1>
              <p className="text-sm md:text-base text-slate-600">Monitor your child's UPSC preparation progress</p>
            </div>
          </div>
        </div>

        {/* Pending links alert removed - links are now auto-approved */}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/80 border border-slate-200 mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="tab-dashboard">
              <Eye className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="children" className="flex items-center gap-2" data-testid="tab-children">
              <Users className="h-4 w-4" />
              My Children
            </TabsTrigger>
            <TabsTrigger value="links" className="flex items-center gap-2" data-testid="tab-links">
              <Shield className="h-4 w-4" />
              Linked Accounts
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {children.length === 0 ? (
              <Card className="bg-white/80">
                <CardContent className="p-8 text-center">
                  <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No linked children yet</p>
                  <Button onClick={() => setActiveTab('links')} className="bg-purple-500 hover:bg-purple-600">
                    Link Your Child's Account
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Child Selector */}
                {children.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {children.map(child => (
                      <Button
                        key={child.student_id}
                        variant={selectedChild?.student_id === child.student_id ? 'default' : 'outline'}
                        onClick={() => setSelectedChild(child)}
                        className="flex-shrink-0"
                      >
                        {child.student_name}
                      </Button>
                    ))}
                  </div>
                )}

                {selectedChild && dashboard && (
                  <>
                    {/* Student Info Card */}
                    <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                              <User className="h-8 w-8" />
                            </div>
                            <div>
                              <h2 className="text-2xl font-bold">{dashboard.student.name}</h2>
                              <p className="text-white/80">{dashboard.student.email}</p>
                              {selectedChild.batch_name && (
                                <p className="text-white/70 text-sm">{selectedChild.batch_name}</p>
                              )}
                            </div>
                          </div>
                          <Badge className={`text-lg px-4 py-2 ${getRiskColor(selectedChild.risk_level)}`}>
                            {selectedChild.risk_level === 'GREEN' ? 'On Track' : 
                             selectedChild.risk_level === 'YELLOW' ? 'Needs Attention' : 'At Risk'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-grid">
                      <Card className="bg-white/80">
                        <CardContent className="p-4 text-center">
                          <Activity className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-blue-600">{dashboard.metrics.consistency_score || 0}%</p>
                          <p className="text-xs text-slate-500">Consistency</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-white/80">
                        <CardContent className="p-4 text-center">
                          <TrendingUp className="h-6 w-6 text-green-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-green-600">{dashboard.metrics.study_intensity || 0}%</p>
                          <p className="text-xs text-slate-500">Study Intensity</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-white/80">
                        <CardContent className="p-4 text-center">
                          <Clock className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-orange-600">{selectedChild.current_streak || 0}</p>
                          <p className="text-xs text-slate-500">Day Streak</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-white/80">
                        <CardContent className="p-4 text-center">
                          <Calendar className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-purple-600">{dashboard.metrics.weekly_hours || 0}h</p>
                          <p className="text-xs text-slate-500">This Week</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Risk Alert */}
                    {dashboard.risk_status.risk_level !== 'GREEN' && (
                      <Card className={`border-2 ${dashboard.risk_status.risk_level === 'RED' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className={`h-5 w-5 ${dashboard.risk_status.risk_level === 'RED' ? 'text-red-500' : 'text-yellow-500'}`} />
                            <div>
                              <p className={`font-medium ${dashboard.risk_status.risk_level === 'RED' ? 'text-red-700' : 'text-yellow-700'}`}>
                                {dashboard.risk_status.risk_level === 'RED' ? 'Your child needs immediate attention' : 'Areas for improvement'}
                              </p>
                              <ul className="text-sm mt-1 space-y-1">
                                {(dashboard.risk_status.reasons || []).map((reason, i) => (
                                  <li key={i} className="text-slate-600">• {reason}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Recent Activity & Upcoming Events */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <Card className="bg-white/80">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Activity className="h-5 w-5 text-blue-500" />
                            Recent Activity
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {dashboard.recent_activity.length === 0 ? (
                            <p className="text-slate-500 text-center py-4">No recent activity</p>
                          ) : (
                            <div className="space-y-3">
                              {dashboard.recent_activity.slice(0, 5).map((event, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                  <div>
                                    <p className="font-medium text-sm capitalize">{event.event_type?.replace(/_/g, ' ')}</p>
                                    <p className="text-xs text-slate-500">{event.module}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-slate-500">{formatDate(event.timestamp)}</p>
                                    <p className="text-xs text-slate-400">{event.duration_minutes || 0} min</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-white/80">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Calendar className="h-5 w-5 text-teal-500" />
                            Upcoming Events
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {dashboard.upcoming_events.length === 0 ? (
                            <p className="text-slate-500 text-center py-4">No upcoming events</p>
                          ) : (
                            <div className="space-y-3">
                              {dashboard.upcoming_events.map((event, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                                  <div>
                                    <p className="font-medium text-sm">{event.title}</p>
                                    <Badge variant="outline" className="text-xs capitalize">{event.event_type}</Badge>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm text-slate-600">{event.start_date}</p>
                                    {event.start_time && <p className="text-xs text-slate-400">{event.start_time}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Interventions */}
                    {dashboard.interventions.length > 0 && (
                      <Card className="bg-white/80 border-orange-200">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <AlertTriangle className="h-5 w-5 text-orange-500" />
                            Active Support Cases
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {dashboard.interventions.map((intv, i) => (
                              <div key={i} className="p-3 bg-orange-50 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <Badge className="capitalize">{intv.intervention_type?.replace(/_/g, ' ')}</Badge>
                                  <Badge variant="outline" className="capitalize">{intv.status}</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mt-2">{intv.reason}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            )}
          </TabsContent>

          {/* Children Tab */}
          <TabsContent value="children">
            <Card className="bg-white/80">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-500" />
                  Linked Children ({children.length})
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchChildren}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {children.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No linked children</p>
                ) : (
                  <div className="space-y-4" data-testid="children-list">
                    {children.map(child => (
                      <div 
                        key={child.student_id}
                        className="p-4 rounded-lg border border-slate-200 hover:border-purple-300 transition-all cursor-pointer"
                        onClick={() => { setSelectedChild(child); setActiveTab('dashboard'); }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                              <User className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{child.student_name}</p>
                              <p className="text-sm text-slate-500">{child.student_email}</p>
                              {child.batch_name && (
                                <p className="text-xs text-slate-400">{child.batch_name}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={getRiskColor(child.risk_level)}>
                              {child.risk_level}
                            </Badge>
                            <p className="text-sm text-slate-500 mt-1">
                              {child.consistency_score}% consistency
                            </p>
                            <p className="text-xs text-slate-400">
                              Last active: {child.last_active ? formatDate(child.last_active) : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Links Tab */}
          <TabsContent value="links">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/80">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-indigo-500" />
                    Link Requests
                  </CardTitle>
                  <Button onClick={() => setShowLinkForm(true)} className="bg-indigo-500 hover:bg-indigo-600">
                    Request Link
                  </Button>
                </CardHeader>
                <CardContent>
                  {myLinks.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No link requests</p>
                  ) : (
                    <div className="space-y-3" data-testid="links-list">
                      {myLinks.map(link => (
                        <div key={link.id} className="p-3 rounded-lg border border-slate-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{link.student_name}</p>
                              <p className="text-sm text-slate-500">{link.student_email}</p>
                            </div>
                            <Badge className={
                              link.status === 'approved' ? 'bg-green-100 text-green-700' :
                              link.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {link.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Link Request Form */}
              {showLinkForm && (
                <Card className="bg-white border-indigo-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Link to Child's Account</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={requestLink} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Child's Email</label>
                        <Input
                          type="email"
                          value={linkForm.student_email}
                          onChange={e => setLinkForm({...linkForm, student_email: e.target.value})}
                          placeholder="child@example.com"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Your Name</label>
                        <Input
                          value={linkForm.parent_name}
                          onChange={e => setLinkForm({...linkForm, parent_name: e.target.value})}
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Phone (optional)</label>
                        <Input
                          value={linkForm.phone}
                          onChange={e => setLinkForm({...linkForm, phone: e.target.value})}
                          placeholder="+91 98765 43210"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1 bg-indigo-500 hover:bg-indigo-600" disabled={loading}>
                          {loading ? 'Sending...' : 'Send Request'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowLinkForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* How it works */}
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                <CardHeader>
                  <CardTitle className="text-lg">How Parent Linking Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs">1</div>
                    <p>Enter your child's registered email address</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs">2</div>
                    <p>Click "Send Request" to link to their account</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs">3</div>
                    <p>Done! You can immediately view their progress</p>
                  </div>
                  <p className="text-slate-500 mt-4">
                    Link is instant - no approval needed. Monitor your child's UPSC preparation journey.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
